import * as React from "react";
import { TripWithTenant } from "../../../../types";

interface TripWithPlacement extends TripWithTenant {
  hangar_id?: string | null;
  ramp_id?: string | null;
  hangar_name?: string | null;
  ramp_name?: string | null;
  location_name?: string | null;
  location_type?: string | null;
}

interface UseOptimisticTripsOptions {
  initialTrips: TripWithPlacement[];
}

/**
 * Manages trips state with optimistic updates
 * Immediately updates UI, then rolls back on error
 */
export const useOptimisticTrips = ({
  initialTrips,
}: UseOptimisticTripsOptions) => {
  const [trips, setTrips] = React.useState<TripWithPlacement[]>(initialTrips);
  const [loadingTripIds, setLoadingTripIds] = React.useState<Set<string>>(
    new Set()
  );

  // Update trips when initial data changes
  React.useEffect(() => {
    setTrips(initialTrips);
  }, [initialTrips]);

  /**
   * Optimistically assign trip to location
   */
  const assignTrip = React.useCallback(
    async (
      tripId: string,
      locationId: string,
      locationType: "hangar" | "ramp",
      locationName: string,
      performUpdate: () => Promise<void>
    ) => {
      // Save previous state for rollback
      const previousTrips = trips;

      // Mark trip as loading
      setLoadingTripIds((prev) => new Set(prev).add(tripId));

      // Optimistic update
      setTrips((prev) =>
        prev.map((trip) => {
          if (trip.trip_id !== tripId) return trip;

          // Update location fields based on type
          if (locationType === "hangar") {
            return {
              ...trip,
              hangar_id: locationId,
              hangar_name: locationName,
              ramp_id: null,
              ramp_name: null,
              location_name: locationName,
              location_type: "hangar",
            };
          } else {
            return {
              ...trip,
              ramp_id: locationId,
              ramp_name: locationName,
              hangar_id: null,
              hangar_name: null,
              location_name: locationName,
              location_type: "ramp",
            };
          }
        })
      );

      try {
        // Perform actual update
        await performUpdate();
      } catch (error) {
        // Rollback on error
        setTrips(previousTrips);
        throw error;
      } finally {
        // Remove loading state
        setLoadingTripIds((prev) => {
          const next = new Set(prev);
          next.delete(tripId);
          return next;
        });
      }
    },
    [trips]
  );

  /**
   * Optimistically unassign trip from location
   */
  const unassignTrip = React.useCallback(
    async (tripId: string, performUpdate: () => Promise<void>) => {
      const previousTrips = trips;
      setLoadingTripIds((prev) => new Set(prev).add(tripId));

      // Optimistic update
      setTrips((prev) =>
        prev.map((trip) => {
          if (trip.trip_id !== tripId) return trip;
          return {
            ...trip,
            hangar_id: null,
            hangar_name: null,
            ramp_id: null,
            ramp_name: null,
            location_name: null,
            location_type: null,
          };
        })
      );

      try {
        await performUpdate();
      } catch (error) {
        setTrips(previousTrips);
        throw error;
      } finally {
        setLoadingTripIds((prev) => {
          const next = new Set(prev);
          next.delete(tripId);
          return next;
        });
      }
    },
    [trips]
  );

  /**
   * Optimistically bulk assign trips to location
   */
  const bulkAssignTrips = React.useCallback(
    async (
      tripIds: string[],
      locationId: string,
      locationType: "hangar" | "ramp",
      locationName: string,
      performUpdate: (tripId: string) => Promise<void>
    ) => {
      const previousTrips = trips;
      const tripIdsSet = new Set(tripIds);

      // Mark all trips as loading
      setLoadingTripIds((prev) => {
        const next = new Set(prev);
        tripIds.forEach((id) => next.add(id));
        return next;
      });

      // Optimistic update
      setTrips((prev) =>
        prev.map((trip) => {
          if (!tripIdsSet.has(trip.trip_id)) return trip;

          if (locationType === "hangar") {
            return {
              ...trip,
              hangar_id: locationId,
              hangar_name: locationName,
              ramp_id: null,
              ramp_name: null,
              location_name: locationName,
              location_type: "hangar",
            };
          } else {
            return {
              ...trip,
              ramp_id: locationId,
              ramp_name: locationName,
              hangar_id: null,
              hangar_name: null,
              location_name: locationName,
              location_type: "ramp",
            };
          }
        })
      );

      try {
        // Perform updates in parallel
        await Promise.all(tripIds.map((tripId) => performUpdate(tripId)));
      } catch (error) {
        // Rollback on error
        setTrips(previousTrips);
        throw error;
      } finally {
        // Remove loading states
        setLoadingTripIds((prev) => {
          const next = new Set(prev);
          tripIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [trips]
  );

  /**
   * Optimistically bulk unassign trips
   */
  const bulkUnassignTrips = React.useCallback(
    async (
      tripIds: string[],
      performUpdate: (tripId: string) => Promise<void>
    ) => {
      const previousTrips = trips;
      const tripIdsSet = new Set(tripIds);

      setLoadingTripIds((prev) => {
        const next = new Set(prev);
        tripIds.forEach((id) => next.add(id));
        return next;
      });

      // Optimistic update
      setTrips((prev) =>
        prev.map((trip) => {
          if (!tripIdsSet.has(trip.trip_id)) return trip;
          return {
            ...trip,
            hangar_id: null,
            hangar_name: null,
            ramp_id: null,
            ramp_name: null,
            location_name: null,
            location_type: null,
          };
        })
      );

      try {
        await Promise.all(tripIds.map((tripId) => performUpdate(tripId)));
      } catch (error) {
        setTrips(previousTrips);
        throw error;
      } finally {
        setLoadingTripIds((prev) => {
          const next = new Set(prev);
          tripIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [trips]
  );

  /**
   * Optimistically delete/cancel trips
   */
  const bulkCancelTrips = React.useCallback(
    async (
      tripIds: string[],
      performUpdate: (tripId: string) => Promise<void>
    ) => {
      const previousTrips = trips;
      const tripIdsSet = new Set(tripIds);

      setLoadingTripIds((prev) => {
        const next = new Set(prev);
        tripIds.forEach((id) => next.add(id));
        return next;
      });

      // Optimistic update - remove trips from list
      setTrips((prev) => prev.filter((trip) => !tripIdsSet.has(trip.trip_id)));

      try {
        await Promise.all(tripIds.map((tripId) => performUpdate(tripId)));
      } catch (error) {
        setTrips(previousTrips);
        throw error;
      } finally {
        setLoadingTripIds((prev) => {
          const next = new Set(prev);
          tripIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [trips]
  );

  /**
   * Optimistically bulk delete trips (hard delete)
   */
  const bulkDeleteTrips = React.useCallback(
    async (
      tripIds: string[],
      performUpdate: (tripId: string) => Promise<void>
    ) => {
      const previousTrips = trips;
      const tripIdsSet = new Set(tripIds);

      setLoadingTripIds((prev) => {
        const next = new Set(prev);
        tripIds.forEach((id) => next.add(id));
        return next;
      });

      // Optimistic update - remove trips from list
      setTrips((prev) => prev.filter((trip) => !tripIdsSet.has(trip.trip_id)));

      try {
        await Promise.all(tripIds.map((tripId) => performUpdate(tripId)));
      } catch (error) {
        setTrips(previousTrips);
        throw error;
      } finally {
        setLoadingTripIds((prev) => {
          const next = new Set(prev);
          tripIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [trips]
  );

  /**
   * Force refresh trips from server
   */
  const refreshTrips = React.useCallback(
    (newTrips: TripWithPlacement[]) => {
      setTrips(newTrips);
    },
    []
  );

  return {
    trips,
    loadingTripIds,
    assignTrip,
    unassignTrip,
    bulkAssignTrips,
    bulkUnassignTrips,
    bulkCancelTrips,
    bulkDeleteTrips,
    refreshTrips,
  };
};
